// feature/fundraising-sadaqah, Mar 6th 2026: Donation fundraising form controller.

import { Controller } from "stimulus"
import { getStripeElementOptions, getElementsOptions, getPaymentElementOptions } from "../utils/stripe_element_options"
import { paymentChallengeToken, resetPaymentChallenge, waitForPaymentChallenge } from "../utils/payment_security"

const MIN_AMOUNT_CENTS = 500

export default class extends Controller {
  static targets = [
    "step1", "step2", "paymentElement", "paymentErrors",
    "nextButton", "step1Actions", "step2Actions", "submitButton",
    "amountInput", "amountCentsHidden", "paymentIntentId", "paymentMethodId", "paymentMethodType"
  ]
  static values = { stripeKey: String, fundraisingPageSlug: String }

  connect() {
    this.stripe = null
    this.elements = null
    this.clientSecret = null
    this.isSubmitting = false
  }

  selectAmount(event) {
    const cents = parseInt(event.currentTarget.dataset.amountCents, 10)
    if (this.hasAmountInputTarget) this.amountInputTarget.value = cents / 100
    if (this.hasAmountCentsHiddenTarget) this.amountCentsHiddenTarget.value = cents
  }

  async nextStep() {
    const validationError = this.validateStep1()
    if (validationError) {
      this.showError(this.paymentErrorsTarget, validationError)
      return
    }

    this.clearError()
    this.nextButtonTarget.disabled = true
    this.showStep(2)
    try {
      await waitForPaymentChallenge(this.element)
      const data = await this.createPaymentIntent()
      if (data.error || !data.client_secret) {
        this.showError(this.paymentErrorsTarget, data.error || 'Could not start payment. Please try again.')
        return
      }
      if (!this.stripeKeyValue) {
        this.showError(this.paymentErrorsTarget, 'Payment is not configured.')
        return
      }

      const { client_secret, payment_intent_id } = data
      this.clientSecret = client_secret
      if (this.hasPaymentIntentIdTarget) this.paymentIntentIdTarget.value = payment_intent_id

      this.mountPaymentElement(client_secret, data.stripe_account_id)
    } catch (err) {
      console.error('Fundraising nextStep error:', err)
      this.showError(this.paymentErrorsTarget, err.message || 'Something went wrong. Please try again.')
    }
    this.nextButtonTarget.disabled = false
  }

  back() {
    this.showStep(1)
    this.clearError()
  }

  validateStep1() {
    const amountCents = this.getAmountCents()
    if (!amountCents || amountCents < MIN_AMOUNT_CENTS) return 'Please enter an amount of at least 5.'
    const first = this.element.querySelector('[name="fundraising_form[first_name]"]')?.value?.trim()
    const last = this.element.querySelector('[name="fundraising_form[last_name]"]')?.value?.trim()
    const email = this.element.querySelector('[name="fundraising_form[email]"]')?.value?.trim()
    if (!first || !last || !email) return 'Please fill in first name, last name, and email.'
    this.amountCentsHiddenTarget.value = amountCents
    return null
  }

  async createPaymentIntent() {
    const response = await fetch('/get_donation_payment_intent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
      },
      body: JSON.stringify({
        fundraising_page_slug: this.fundraisingPageSlugValue,
        amount_cents: parseInt(this.amountCentsHiddenTarget.value, 10),
        first_name: this.formValue('first_name'),
        last_name: this.formValue('last_name'),
        email: this.formValue('email'),
        payment_challenge_token: paymentChallengeToken(this.element)
      })
    })
    resetPaymentChallenge(this.element)
    return response.json()
  }

  mountPaymentElement(clientSecret, stripeAccountId) {
    const stripeOptions = stripeAccountId ? { stripeAccount: stripeAccountId } : {}
    this.stripe = window.Stripe(this.stripeKeyValue, stripeOptions)
    const elementOptions = getStripeElementOptions('digital_wallets')
    const elementsOptions = getElementsOptions(elementOptions)
    this.elements = this.stripe.elements({ clientSecret, ...elementsOptions })
    const paymentElement = this.elements.create('payment', {
      ...getPaymentElementOptions(elementOptions),
      layout: { type: 'accordion', defaultCollapsed: false }
    })
    requestAnimationFrame(() => {
      this.paymentElementTarget.innerHTML = ''
      paymentElement.mount(this.paymentElementTarget)
    })
  }

  showStep(step) {
    const isStep1 = step === 1
    this.step1Target.classList.toggle('is-hidden', !isStep1)
    this.step2Target.classList.toggle('is-hidden', isStep1)
    if (this.hasStep1ActionsTarget) this.step1ActionsTarget.classList.toggle('is-hidden', !isStep1)
    if (this.hasStep2ActionsTarget) this.step2ActionsTarget.classList.toggle('is-hidden', isStep1)
  }

  async submitPayment(event) {
    if (this.isSubmitting) return
    if (!this.elements || !this.clientSecret) return

    this.clearError()
    const submitResult = await this.elements.submit()
    if (submitResult?.error) {
      const msg = submitResult.error?.message || 'Please complete all required fields.'
      this.showError(this.paymentErrorsTarget, msg)
      return
    }

    this.isSubmitting = true
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = true

    try {
      const result = await this.stripe.confirmPayment({
        elements: this.elements,
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: {
            billing_details: {
              name: [this.formValue('first_name'), this.formValue('last_name')].filter(Boolean).join(' '),
              email: this.formValue('email')
            }
          }
        },
        redirect: 'if_required'
      })

      if (result.error) {
        this.showError(this.paymentErrorsTarget, result.error.message || 'Payment failed.')
        this.isSubmitting = false
        if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = false
        return
      }

      if (result.paymentIntent?.status === 'succeeded') {
        this.paymentMethodIdTarget.value = result.paymentIntent.payment_method
        this.paymentMethodTypeTarget.value = result.paymentIntent.payment_method_types?.[0] || 'card'
        this.element.submit()
      }
    } catch (err) {
      console.error('Fundraising submitPayment error:', err)
      this.showError(this.paymentErrorsTarget, err.message || 'Payment failed. Please try again.')
    }
    this.isSubmitting = false
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = false
  }

  formValue(field) {
    return this.element.querySelector(`[name="fundraising_form[${field}]"]`)?.value?.trim() || undefined
  }

  getAmountCents() {
    if (this.hasAmountCentsHiddenTarget && this.amountCentsHiddenTarget.value) {
      return parseInt(this.amountCentsHiddenTarget.value, 10)
    }
    if (!this.hasAmountInputTarget) return 0
    const v = this.amountInputTarget.value
    const num = parseFloat(v, 10)
    if (Number.isNaN(num)) return 0
    return Math.round(num * 100)
  }

  showError(container, message) {
    if (!container) return
    container.textContent = message
    container.classList.remove('is-hidden')
  }

  clearError() {
    if (this.hasPaymentErrorsTarget) {
      this.paymentErrorsTarget.textContent = ''
      this.paymentErrorsTarget.classList.add('is-hidden')
    }
  }
}
